import { type RootState } from '@/app-state/store';
import _ from 'lodash';

import buildFilterOptions from '@/components/vehicle-detail-header-filter/utils/build-filter-options';
import getNextFilter from '@/components/vehicle-detail-header-filter/utils/get-next-filter';
import getRemainingVariants from '@/components/vehicle-detail-header-filter/utils/get-remaining-variants';
import { STATIC_ATTRIBUTE } from '@/components/vehicle-detail-header-filter/utils/constants';
import { type HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';
import { type Action } from '@rematch/core';

type SelectedOption = {
  id?: string;
  isBaseOption?: boolean;
  value?: string | null;
  sequenceNumber?: number;
};

const refreshFilters = (state: RootState, selectedOption?: SelectedOption, retainedFilters = []) => {
  const { vehicles } = state;
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type '"desc"' is not assignable to parameter of type 'undefined'.
  const options = buildFilterOptions(vehicles, STATIC_ATTRIBUTE, true, 'desc');

  const staticFilterOption = selectedOption || _.first(options);
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'STATIC_ATTRIBUTE' is possibly 'undefined'.
  let dynamicVariants = getRemainingVariants(STATIC_ATTRIBUTE.name, staticFilterOption, vehicles);

  if (!_.isEmpty(retainedFilters)) {
    let filtersRestored = 0;
    while (filtersRestored < retainedFilters.length) {
      const { label, selectedOption } = retainedFilters[filtersRestored];
      dynamicVariants = getRemainingVariants(label, selectedOption, dynamicVariants);
      filtersRestored += 1;
    }
  }

  const nextFilter = getNextFilter(dynamicVariants, retainedFilters);

  return {
    ...state,
    variants: dynamicVariants,
    staticFilter: {
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'STATIC_ATTRIBUTE' is possibly 'undefined'.
      label: STATIC_ATTRIBUTE.name,
      options,
      selectedOption: staticFilterOption,
    },
    dynamicFilters: nextFilter ? [...retainedFilters, nextFilter] : retainedFilters,
  };
};

type StaticFilter = {
  event: {
    label: string;
    value: string;
  };
};

type DynamicFilter = {
  event: {
    label: string;
    value: string;
    filterLabel: string;
  };
  index: number;
};

const updateStaticFilter = (state: RootState, { event }: StaticFilter) => {
  const { value, label } = event;

  const selectedOption = {
    id: value,
    value: label,
  };

  return refreshFilters(state, selectedOption);
};

const updateDynamicFilters = (state: RootState, { event, index }: DynamicFilter) => {
  const { staticFilter, dynamicFilters, variants } = state;

  const selectedLabel = event.filterLabel;

  const selectedOption = {
    id: event.value,
    value: event.label,
  };

  // @ts-expect-error [PLEASE FIX]: Property 'length' does not exist on type 'never'.
  if (index === dynamicFilters.length - 1 && _.size(variants) > 1) {
    // console.log(`User updated the last filter selection.`);
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Spread types may only be created from object types.
    const lastUpdatedDynamicFilter = { ..._.last(dynamicFilters), selectedOption };
    // console.log(`Last filter's selected option updated to: `, lastUpdatedDynamicFilter.selectedOption);
    const updatedDynamicFilters = _.concat(_.slice(dynamicFilters, 0, index), lastUpdatedDynamicFilter);
    const updatedVariants = getRemainingVariants(selectedLabel, selectedOption, variants);
    // console.log(`...variants remaining based on selection from the last filter: `, updatedVariants);
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type 'any[]' is not assignable to parameter of type 'never[]'.
    const nextFilter = getNextFilter(updatedVariants, updatedDynamicFilters);
    // console.log(`...next filter based on updated current filters and variants: `, nextFilter);

    return {
      ...state,
      dynamicFilters: nextFilter ? [...updatedDynamicFilters, nextFilter] : updatedDynamicFilters,
      variants: updatedVariants,
    };
  }

  // console.log(`User edited earlier dynamic filter selection`);

  const updatedDynamicFilters = _.map(dynamicFilters, (filter) => {
    // @ts-expect-error [PLEASE FIX]: Property 'options' does not exist on type 'never'. Property 'label' does not exist on type 'never'. Spread types may only be created from object types.
    return filter.label === event.filterLabel ? { ...filter, selectedOption } : filter;
  });

  const retainedDynamicFilters = _.slice(updatedDynamicFilters, 0, index + 1);
  // console.log(`...save current filter selections and re-initialize variants and filters.`);

  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type 'any[]' is not assignable to parameter of type 'never[]'.
  return refreshFilters(state, staticFilter.selectedOption, retainedDynamicFilters);
};

type InitFilterInput = {
  vehicles: HsiycSearchVehiclesRecord[];
  pageVehicle: HsiycSearchVehiclesRecord;
};

const init = (state: RootState, { vehicles, pageVehicle }: InitFilterInput) => {
  const stateWithVehicles = { ...state, vehicles };
  const selectedOption = {
    id: undefined,
    value: pageVehicle.year,
  };

  // @ts-expect-error [PLEASE FIX]: Argument of type '{ vehicles: HsiycSearchVehiclesRecord[]; cache: CacheState; compare: CompareState; filters: FiltersState; ui: UiState; }' is not assignable to parameter of type 'RootState'. Type '{ vehicles: HsiycSearchVehiclesRecord[]; cache: CacheState; compare: CompareState; filters: FiltersState; ui: UiState; }' is not assignable to type '{ [x: string]: never; }'. Property 'vehicles' is incompatible with index signature. Type 'HsiycSearchVehiclesRecord[]' is not assignable to type 'never'. [2345]
  return refreshFilters(stateWithVehicles, selectedOption);
};

const vehicleFiltersReducer = (state: RootState, action: Action) => {
  switch (action.type) {
    case 'FILTERS_INIT':
      return init(state, action.payload);
    case 'FILTERS_UPDATE_STATIC':
      return updateStaticFilter(state, action.payload);
    case 'FILTERS_UPDATE_DYNAMIC':
      return updateDynamicFilters(state, action.payload);
    default:
      throw new Error('Unknown reducer action type.');
  }
};

export { init, vehicleFiltersReducer, refreshFilters, updateDynamicFilters, updateStaticFilter };
